import React, { useEffect, useState } from "react";
import {
  CWidgetStatsA,
  CBadge,
  CButton,
  CSpinner,
  CWidgetStatsD,
  CRow,
  CCol,
  CContainer,
  CCard,
  CCardBody,
  CTable,
  CTableHead,
  CTableRow,
  CTableHeaderCell,
  CTableBody,
  CTableDataCell,
} from "@coreui/react";
import {
  cilBriefcase,
  cilBullhorn,
  cilBank,
  cilCloudDownload,
} from "@coreui/icons";
import { CChartLine } from "@coreui/react-chartjs";
import CIcon from "@coreui/icons-react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import "highcharts/modules/funnel";
import "highcharts/modules/streamgraph";
import "highcharts/modules/treemap";
import "highcharts/modules/pattern-fill";
import "highcharts/modules/no-data-to-display";
// import "highcharts/modules/packed-bubble";
// import 'highcharts/modules/solid-gauge';
import "highcharts/highcharts-more";
import "highcharts/modules/map";
import portugalMapData from "@highcharts/map-collection/countries/pt/pt-all.geo.json";

import { apiClient } from "services/Api";
import ErrorPage from "components/globals/ErrorPage";
import LoadingScreen from "components/globals/LoadingScreen";

portugalMapData.features[2].properties.region = "Centro";
Highcharts.setOptions({
  lang: {
    noData: "Sem dados para apresentar.",
  },
  noData: {
    style: {
      fontWeight: "600",
      color: "#64748b",
      fontSize: "0.95rem",
    },
  },
});

const Kpi = () => {
  const [data, setData] = useState(null);
  const [filters, setFilters] = useState({});
  const [error, setError] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfError, setPdfError] = useState("");

  useEffect(() => {
    apiClient
      .get("/projects/kpis/")
      .then((response) => {
        setData(response.data);
      })
      .catch(() => {
        setError(true);
      });
  }, []);

  if (error)
    return <ErrorPage message="Erro ao carregar os dados da Teamwork." />;

  if (!data) return <LoadingScreen message="A carregar dados da Teamwork" />;

  const getSemester = (dateStr) => {
    if (!dateStr) return null;

    const parts = dateStr.split("/");
    const day = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10);
    const year = parts[2];
    // Considera S1: janeiro a junho, S2: julho a dezembro
    const semester = month <= 6 ? "S1" : "S2";
    return `${year}-${semester}`;
  };

  const toggleFilter = (filter, value) => {
    setFilters((prevFilters) => {
      const newFilters = { ...prevFilters };
      if (newFilters[filter] === value) {
        delete newFilters[filter]; // Remove o filtro se já estiver ativo
      } else {
        newFilters[filter] = value; // Aplica o filtro
      }
      return newFilters;
    });
  };

  const clearFilters = () => setFilters({});

  const filterLabels = {
    state: "Estado",
    typology: "Tipologia",
    sector: "Setor",
    notice: "Aviso",
    semesterRange: "Semestre",
    company_size: "Dimensao",
    region: "Regiao",
  };

  const formatSemesterRange = (range) => {
    if (!range?.from) return "-";
    if (!range?.to) return `${range.from} ->`;
    return `${range.from} -> ${range.to}`;
  };

  const activeFilters = Object.entries(filters).map(([key, value]) => ({
    key,
    label: filterLabels[key] || key,
    value: key === "semesterRange" ? formatSemesterRange(value) : value,
  }));

  const semesterIndex = (semester) => {
    if (!semester) return null;
    const [year, half] = semester.split("-");
    if (!year || !half) return null;
    const step = half === "S2" ? 1 : 0;
    return parseInt(year, 10) * 2 + step;
  };

  const filteredData = data.filter((project) => {
    if (filters.state && !project.states.includes(filters.state)) {
      return false;
    }

    if (filters.typology && project.typology !== filters.typology) {
      return false;
    }

    if (filters.sector && project.sector !== filters.sector) {
      return false;
    }

    if (filters.notice && project.notice !== filters.notice) {
      return false;
    }

    if (filters.semesterRange?.from) {
      const current = semesterIndex(getSemester(project.submission_date));
      const from = semesterIndex(filters.semesterRange.from);
      const to = semesterIndex(filters.semesterRange.to);
      if (current == null || from == null) return false;
      if (to != null && (current < from || current > to)) return false;
      if (to == null && current < from) return false;
    }

    if (filters.company_size && project.company_size !== filters.company_size) {
      return false;
    }

    if (filters.region && project.region !== filters.region) {
      return false;
    }

    return true;
  });
  const hasProjects = filteredData.length > 0;

  const groupDataBySemester = (data, dateField, field) => {
    const groups = {};
    data.forEach((item) => {
      const dateStr = item[dateField]; // ex: "09/09/2024"
      if (!dateStr?.trim()) return;

      const [day, month, year] = dateStr.split("/");
      const semester = month <= 6 ? "1S" : "2S";
      const key = `${semester} ${year}`;

      groups[key] = groups[key] || {
        sum: 0,
        date: new Date(year, month - 1, day),
      };
      groups[key].sum += parseFloat(item.financials[field]) || 0;
    });

    // Ordenar as chaves pela data
    const sortedKeys = Object.keys(groups).sort(
      (a, b) => groups[a].date - groups[b].date,
    );
    const labels = sortedKeys;
    const sumData = sortedKeys.map((key) => groups[key].sum);
    return { labels, sumData };
  };

  // Função para interpolar entre duas cores (de vermelho para verde)
  const getColorForPercentage = (perc) => {
    // Valores em RGB para 0% e 100%
    const start = { r: 220, g: 53, b: 69 }; // vermelho (bootstrap danger)
    const end = { r: 40, g: 167, b: 69 }; // verde (bootstrap success)
    const ratio = Math.min(Math.max(perc / 100, 0), 1);
    const r = Math.round(start.r + (end.r - start.r) * ratio);
    const g = Math.round(start.g + (end.g - start.g) * ratio);
    const b = Math.round(start.b + (end.b - start.b) * ratio);
    return `rgb(${r},${g},${b})`;
  };

  const formatCurrencyCompact = (value) => {
    const number = Number(value) || 0;
    return new Intl.NumberFormat("pt-PT", {
      style: "currency",
      currency: "EUR",
      notation: "compact",
      maximumFractionDigits: 1,
    }).format(number);
  };

  const formatNumberCompact = (value) => {
    const number = Number(value) || 0;
    return new Intl.NumberFormat("pt-PT", {
      notation: "compact",
      maximumFractionDigits: 1,
    }).format(number);
  };

  const cardClass = "shadow-sm border-0";
  const cardBodyClass = "p-3";

  const stateColors = {
    Submetidos: "#4e79a7",
    "Em Análise": "#f28e2b",
    Avaliados: "#e15759",
    Aprovados: "#59a14f",
    Reprovados: "#edc949",
    Desistidos: "#b07aa1",
    "Em Gestão": "#76b7b2",
    "Em Encerramento": "#ff9da7",
    Encerrados: "#9c755f",
  };

  const typologyColors = {
    "SI Inovação": "#2f5aa6",
    "SI I&DT": "#1f8a3f",
    "SI Internacionalização": "#d19a00",
    PRR: "#7f56b8",
    Outros: "#89CFF0",
  };

  const getTypologyColor = (name) => typologyColors[name] || "#64748b";
  const getStateColor = (name) => stateColors[name] || "#64748b";

  const hexToRgb = (hex) => {
    const normalized = hex.replace("#", "");
    const bigint = parseInt(normalized, 16);
    return {
      r: (bigint >> 16) & 255,
      g: (bigint >> 8) & 255,
      b: bigint & 255,
    };
  };

  const mixColor = (hex, ratio = 0.2) => {
    const { r, g, b } = hexToRgb(hex);
    const mix = (c) => Math.round(c + (255 - c) * ratio);
    return `rgb(${mix(r)},${mix(g)},${mix(b)})`;
  };

  const getNoticeGradient = (typology) => {
    const base = getTypologyColor(typology);
    return {
      linearGradient: { x1: 0, y1: 0, x2: 1, y2: 1 },
      stops: [
        [0, base],
        [1, mixColor(base, 0.35)],
      ],
    };
  };

  const getNoticeBadgeStyle = (typology) => {
    const base = getTypologyColor(typology);
    return {
      background: `linear-gradient(135deg, ${base}, ${mixColor(base, 0.35)})`,
      color: "#fff",
    };
  };

  const sectorOrder = filteredData.reduce((acc, d) => {
    const name = d.sector || "Sem setor";
    if (!acc.includes(name)) acc.push(name);
    return acc;
  }, []);

  const totalProjects = filteredData.length;
  const financials = filteredData.reduce(
    (acc, item) => {
      const investimentoCandidatura =
        parseFloat(item.financials["Investimento Candidatura"]) || 0;
      const investimentoElegivel =
        parseFloat(item.financials["Investimento Elegível"]) || 0;
      const investimentoElegivelCandidatura =
        parseFloat(item.financials["Investimento Elegível Candidatura"]) || 0;
      const incentivoPrevisto =
        parseFloat(item.financials["Incentivo Previsto"]) || 0;
      const incentivoElegivel =
        parseFloat(item.financials["Incentivo Elegível"]) || 0;

      return {
        totalInvestimentoCandidatura:
          acc.totalInvestimentoCandidatura + investimentoCandidatura,
        totalInvestimentoElegivel:
          acc.totalInvestimentoElegivel + investimentoElegivel,
        totalInvestimentoElegivelCandidatura:
          acc.totalInvestimentoElegivelCandidatura +
          investimentoElegivelCandidatura,
        totalIncentivoPrevisto: acc.totalIncentivoPrevisto + incentivoPrevisto,
        totalIncentivoElegivel: acc.totalIncentivoElegivel + incentivoElegivel,
      };
    },
    {
      totalInvestimentoCandidatura: 0,
      totalInvestimentoElegivelCandidatura: 0,
      totalInvestimentoElegivel: 0,
      totalIncentivoPrevisto: 0,
      totalIncentivoElegivel: 0,
    },
  );

  const groupedInvest = groupDataBySemester(
    filteredData,
    "submission_date",
    "Investimento Candidatura",
  );
  const groupedCandIncentive = groupDataBySemester(
    filteredData,
    "submission_date",
    "Incentivo Previsto",
  );
  const groupedApprovedIncentive = groupDataBySemester(
    filteredData,
    "decision_date",
    "Incentivo Elegível",
  );

  const approvalIncentiveRate =
    financials.totalIncentivoPrevisto > 0
      ? (financials.totalIncentivoElegivel /
          financials.totalIncentivoPrevisto) *
        100
      : 0;

  const approvalInvestRate =
    financials.totalInvestimentoCandidatura > 0
      ? (financials.totalInvestimentoElegivel /
          financials.totalInvestimentoCandidatura) *
        100
      : 0;

  const chartDataInvest = {
    labels: groupedInvest.labels,
    datasets: [
      {
        backgroundColor: "rgba(255,255,255,0.1)",
        borderColor: "rgba(255,255,255,0.55)",
        pointHoverBackgroundColor: "#fff",
        borderWidth: 2,
        data: groupedInvest.sumData,
        fill: true,
      },
    ],
  };

  const chartDataCandidatura = {
    labels: groupedCandIncentive.labels,
    datasets: [
      {
        backgroundColor: "rgba(255,255,255,0.1)",
        borderColor: "rgba(255,255,255,0.55)",
        pointHoverBackgroundColor: "#fff",
        borderWidth: 2,
        data: groupedCandIncentive.sumData,
        fill: true,
      },
    ],
  };

  const chartDataApproved = {
    labels: groupedApprovedIncentive.labels,
    datasets: [
      {
        backgroundColor: "rgba(255,255,255,0.1)",
        borderColor: "rgba(255,255,255,0.55)",
        pointHoverBackgroundColor: "#fff",
        borderWidth: 2,
        data: groupedApprovedIncentive.sumData,
        fill: true,
      },
    ],
  };

  // Opções comuns para os gráficos
  const chartOptions = {
    elements: {
      line: {
        tension: 0.4,
      },
      point: {
        radius: 0,
        hitRadius: 10,
        hoverRadius: 4,
        hoverBorderWidth: 3,
      },
    },
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
    },
    scales: {
      x: {
        display: false,
      },
      y: {
        display: false,
      },
    },
  };

  const colors = Highcharts.getOptions().colors;

  const funnelOptions = {
    chart: {
      type: "funnel", // Aumenta a margem para labels externas se necessário
    },
    title: {
      text: "",
    },
    exporting: {
      enabled: false,
    },
    credits: {
      enabled: false,
    },
    plotOptions: {
      series: {
        dataLabels: {
          enabled: true,
          inside: true, // Prioriza colocar as labels dentro
          allowOverlap: true, // Evita que labels fiquem sobrepostas
          format: "<b>{point.name}</b>: {point.y}",
          style: {
            fontSize: "10px",
            fontWeight: "bold",
            color: "#fff",
            // textOutline: 'none',
          },
          distance: -30, // Tenta manter dentro, mas ajusta se necessário
        },
      },
      funnel: {
        allowPointSelect: true,
        cursor: "pointer",
        point: {
          events: {
            click: function () {
              toggleFilter("state", this.name);
            },
          },
        },
      },
    },
    series: hasProjects
      ? [
          {
            name: "Projetos",
            data: [
              {
                name: "Submetidos",
                y: filteredData.reduce(
                  (sum, p) => sum + p.states.includes("Submetidos"),
                  0,
                ),
                color: getStateColor("Submetidos"),
              },
              {
                name: "Em Análise",
                y: filteredData.reduce(
                  (sum, p) => sum + p.states.includes("Em Análise"),
                  0,
                ),
                color: getStateColor("Em Análise"),
              },
              {
                name: "Avaliados",
                y: filteredData.reduce(
                  (sum, p) => sum + p.states.includes("Avaliados"),
                  0,
                ),
                color: getStateColor("Avaliados"),
              },
              {
                name: "Aprovados",
                y: filteredData.reduce(
                  (sum, p) => sum + p.states.includes("Aprovados"),
                  0,
                ),
                color: getStateColor("Aprovados"),
              },
              {
                name: "Reprovados",
                y: filteredData.reduce(
                  (sum, p) => sum + p.states.includes("Reprovados"),
                  0,
                ),
                color: getStateColor("Reprovados"),
              },
              {
                name: "Desistidos",
                y: filteredData.reduce(
                  (sum, p) => sum + p.states.includes("Desistidos"),
                  0,
                ),
                color: getStateColor("Desistidos"),
              },
              {
                name: "Em Gestão",
                y: filteredData.reduce(
                  (sum, p) => sum + p.states.includes("Em Gestão"),
                  0,
                ),
                color: getStateColor("Em Gestão"),
              },
              {
                name: "Em Encerramento",
                y: filteredData.reduce(
                  (sum, p) => sum + p.states.includes("Em Encerramento"),
                  0,
                ),
                color: getStateColor("Em Encerramento"),
              },
              {
                name: "Encerrados",
                y: filteredData.reduce(
                  (sum, p) => sum + p.states.includes("Encerrados"),
                  0,
                ),
                color: getStateColor("Encerrados"),
              },
            ],
          },
        ]
      : [
          {
            name: "Projetos",
            data: [],
          },
        ],
  };

  const groupedTypologies = filteredData.reduce((acc, p) => {
    acc[p.typology] = acc[p.typology] || 0;
    acc[p.typology] += 1;
    return acc;
  }, {});

  const barOptions = {
    chart: {
      type: "column",
    },
    exporting: {
      enabled: false,
    },
    credits: {
      enabled: false,
    },
    title: {
      text: "",
    },
    xAxis: {
      categories: Object.keys(groupedTypologies),
    },
    yAxis: {
      min: 0,
      title: {
        text: "Projetos",
      },
    },
    plotOptions: {
      column: {
        cursor: "pointer",
        point: {
          events: {
            click: function () {
              toggleFilter("typology", this.category);
            },
          },
        },
      },
    },
    series: hasProjects
      ? [
          {
            name: "Projetos",
            data: Object.keys(groupedTypologies).map((name) => ({
              name,
              y: groupedTypologies[name],
              color: getTypologyColor(name),
            })),
          },
        ]
      : [
          {
            name: "Projetos",
            data: [],
          },
        ],
    legend: {
      enabled: false,
    },
  };

  const donutOptions = {
    chart: {
      type: "treemap",
    },
    exporting: {
      enabled: false,
    },
    title: {
      text: "",
    },
    credits: {
      enabled: false,
    },
    tooltip: {
      pointFormat: "{point.name}: <b>{point.value}</b>",
    },
    plotOptions: {
      treemap: {
        layoutAlgorithm: "squarified",
        dataLabels: {
          enabled: true,
          format: "{point.name}: {point.value}",
        },
        allowPointSelect: true, // Permite seleção
        cursor: "pointer",
        point: {
          events: {
            click: function () {
              toggleFilter("sector", this.name); // Aplica ou remove o filtro
            },
          },
        },
      },
    },
    series: hasProjects
      ? [
          {
            name: "Setor",
            data: filteredData
              .reduce((acc, d) => {
                const name = d.sector || "Sem setor";
                const index = acc.findIndex((item) => item.name === name);
                if (index > -1) acc[index].value += 1;
                else acc.push({ name, value: 1 });
                return acc;
              }, [])
              .map((item) => ({
                ...item,
                color: colors[sectorOrder.indexOf(item.name) % colors.length],
              })),
          },
        ]
      : [
          {
            name: "Setor",
            data: [],
          },
        ],
  };

  const noticeCounts = filteredData.reduce((acc, { notice, typology }) => {
    if (!acc[notice]) acc[notice] = {};
    acc[notice][typology] = (acc[notice][typology] || 0) + 1;
    return acc;
  }, {});

  // Agrupar os dados por "type" (tipologia)
  const typeCounts = filteredData.reduce((acc, { typology }) => {
    acc[typology] = (acc[typology] || 0) + 1;
    return acc;
  }, {});

  const noticeData = [];
  Object.keys(noticeCounts).forEach((notice) => {
    const typeData = Object.keys(noticeCounts[notice]).map((typology) => ({
      name: typology,
      y: noticeCounts[notice][typology],
    }));

    const dominantTypology = typeData.reduce(
      (acc, cur) => (cur.y > acc.y ? cur : acc),
      { name: "Outros", y: 0 },
    ).name;

    noticeData.push({
      name: notice,
      y: typeData.reduce((sum, item) => sum + item.y, 0), // Total de tipologias para o aviso
      color: getNoticeGradient(dominantTypology),
      data: typeData,
    });
  });

  const typeData = Object.keys(typeCounts).map((typology) => ({
    name: typology,
    y: typeCounts[typology],
    color: getTypologyColor(typology),
  }));

  const donut2Options = {
    chart: {
      type: "pie",
    },
    exporting: {
      enabled: false,
    },
    credits: {
      enabled: false,
    },
    title: {
      text: "",
    },
    tooltip: {
      pointFormat: "{series.name}: <b>{point.y}</b>",
    },
    plotOptions: {
      pie: {
        innerSize: "50%",
        depth: 35,
        dataLabels: {
          enabled: true,
          format: "{point.name}: {point.y}", // Mostra o nome do tipo e o valor
        },
        point: {
          events: {
            click: function () {
              // Garante que só filtra se for um aviso
              if (this.series.name === "Avisos") {
                toggleFilter("notice", this.name);
              }
            },
          },
        },
      },
    },
    series: hasProjects
      ? [
          {
            name: "Tipologias",
            data: typeData, // Série de dados das tipologias
            size: "50%",
            innerSize: "50%", // Tornar o gráfico interno mais estreito para a segunda série
            showInLegend: false,
          },
          {
            name: "Avisos",
            data: noticeData, // Série de dados dos avisos
            size: "70%",
          },
        ]
      : [
          {
            name: "Tipologias",
            data: [],
            size: "50%",
            innerSize: "50%",
            showInLegend: false,
          },
          {
            name: "Avisos",
            data: [],
            size: "70%",
          },
        ],
  };

  const radialOptions = {
    chart: {
      type: "column",
      inverted: true,
      polar: true,
    },
    title: { text: "Tipologia" },
    series: [
      {
        name: "Tipologia",
        data: filteredData.reduce((acc, d) => {
          const index = acc.findIndex((item) => item.name === d.typology);
          if (index > -1) acc[index].y += 1;
          else acc.push({ name: d.typology, y: 1 });
          return acc;
        }, []),
      },
    ],
  };

  // Agrega os dados: cria buckets para cada ano-semestre com a contagem por tipologia
  const buckets = {}; // { "2024-S1": { "Outros": 0, ... }, "2024-S2": { "Outros": 1, ... } }
  const typologies = new Set();

  filteredData.forEach((project) => {
    const bucket = getSemester(project.submission_date);
    if (!bucket) return;

    if (!buckets[bucket]) {
      buckets[bucket] = {};
    }
    // Regista a tipologia
    typologies.add(project.typology);
    buckets[bucket][project.typology] =
      (buckets[bucket][project.typology] || 0) + 1;
  });

  // Ordena os buckets cronologicamente (assumindo que o formato "YYYY-SX" ordena corretamente)
  const sortedBuckets = Object.keys(buckets).sort();

  // Cria a estrutura de dados para cada série (cada tipologia) com os valores para cada bucket
  const seriesData = Array.from(typologies).map((typology) => {
    return {
      name: typology,
      color: getTypologyColor(typology),
      data: sortedBuckets.map((bucket) => buckets[bucket][typology] || 0),
    };
  });
  const handleSemesterRange = (clicked) => {
    setFilters((prev) => {
      const range = prev.semesterRange || {};
      if (!range.from || (range.from && range.to)) {
        return { ...prev, semesterRange: { from: clicked, to: null } };
      }
      if (range.from === clicked) {
        const next = { ...prev };
        delete next.semesterRange;
        return next;
      }
      const fromIdx = semesterIndex(range.from);
      const toIdx = semesterIndex(clicked);
      if (fromIdx == null || toIdx == null) {
        return { ...prev, semesterRange: { from: clicked, to: null } };
      }
      const [from, to] =
        fromIdx <= toIdx ? [range.from, clicked] : [clicked, range.from];
      return { ...prev, semesterRange: { from, to } };
    });
  };

  const setSemesterRangeFromAxis = (min, max) => {
    if (!sortedBuckets.length) return;
    const minIndex = Math.max(0, Math.floor(min));
    const maxIndex = Math.min(sortedBuckets.length - 1, Math.ceil(max));
    const from = sortedBuckets[minIndex];
    const to = sortedBuckets[maxIndex];
    if (!from || !to) return;
    setFilters((prev) => ({
      ...prev,
      semesterRange: { from: from.replace(" ", "-"), to: to.replace(" ", "-") },
    }));
  };

  const streamgraphOptions = {
    chart: {
      type: "streamgraph", // Certifica-te de incluir o módulo de streamgraph do Highcharts se necessário
      zoomType: "x", // Permite zoom horizontal
      events: {
        selection: function (event) {
          const axis = event?.xAxis?.[0];
          if (!axis) return false;
          setSemesterRangeFromAxis(axis.min, axis.max);
          return false;
        },
      },
    },
    exporting: {
      enabled: false,
    },
    credits: {
      enabled: false, // Remove o branding "Highcharts.com"
    },
    title: {
      text: "",
    },
    xAxis: {
      categories: sortedBuckets,
      title: {
        text: "Semestre",
      },
    },
    yAxis: {
      title: {
        text: "Projetos",
      },
      min: 0,
    },
    tooltip: {
      shared: true,
      valueSuffix: " projetos",
    },
    plotOptions: {
      series: {
        cursor: "pointer",
        point: {
          events: {
            click: function () {
              const category = this.category;
              const semester = category?.replace(" ", "-");
              if (semester) {
                handleSemesterRange(semester);
              }
            },
          },
        },
      },
    },
    series: hasProjects ? seriesData : [{ name: "Tipologia", data: [] }],
  };

  const regionCounts = filteredData.reduce((acc, cur) => {
    const region = cur.region?.toLowerCase();
    acc[region] = (acc[region] || 0) + 1;
    return acc;
  }, {});

  // Preparar os dados para o mapa:
  // Cada objeto deve ter uma propriedade (ex: name) que corresponda à propriedade do mapa (por exemplo, "centro")
  const mapData = [
    ["pt-fa", regionCounts["algarve"] || 0],
    ["pt-li", regionCounts["lisboa"] || 0],
    ["pt-av", regionCounts["centro"] || 0],
    ["pt-vc", regionCounts["norte"] || 0],
    ["pt-be", regionCounts["alentejo"] || 0],
    ["pt-ev", regionCounts["alentejo"] || 0],
    ["pt-se", regionCounts["lisboa"] || 0],
    ["pt-pa", regionCounts["alentejo"] || 0],
    ["pt-sa", regionCounts["alentejo"] || 0],
    ["pt-br", regionCounts["norte"] || 0],
    ["pt-le", regionCounts["centro"] || 0],
    ["pt-ba", regionCounts["norte"] || 0],
    ["pt-cb", regionCounts["centro"] || 0],
    ["pt-gu", regionCounts["centro"] || 0],
    ["pt-co", regionCounts["centro"] || 0],
    ["pt-po", regionCounts["norte"] || 0],
    ["pt-vi", regionCounts["centro"] || 0],
    ["pt-vr", regionCounts["norte"] || 0],
    ["pt-ma", regionCounts["ilhas"] || 0],
    ["pt-ac", regionCounts["ilhas"] || 0],
  ];
  // const mapData = [
  //     { "name": 'pt-vi', value: regionCounts['norte'] || 0 },
  //     { "hc-key": 'pt-av', value: regionCounts['norte'] || 0 },
  //     { "hc-key": 'pt-cb', value: regionCounts['centro'] || 0 },
  //     { "hc-key": 'pt-li', value: regionCounts['lisboa'] || 0 },
  //     { "hc-key": 'pt-ev', value: regionCounts['alentejo'] || 0 },
  //     { "hc-key": 'pt-fa', value: regionCounts['algarve'] || 0 },
  // ];
  // const mapData = [
  //     { "region": "Norte", value: regionCounts['norte'] || 0 },
  //     { "region": "Centro", value: regionCounts['centro'] || 0 },
  //     { "region": "Lisbon", value: regionCounts['lisboa'] || 0 },
  //     { "region": "Alentejo", value: regionCounts['alentejo'] || 0 },
  //     { "region": "Algarve", value: regionCounts['algarve'] || 0 },
  // ];

  const mapOptions = {
    chart: {
      map: portugalMapData,
      backgroundColor: "transparent",
    },
    exporting: {
      enabled: false,
    },
    credits: {
      enabled: false,
    },
    title: {
      text: "",
    },
    subtitle: hasProjects ? undefined : { text: "Sem dados para apresentar." },
    legend: {
      enabled: true,
      align: "right",
      verticalAlign: "middle",
      layout: "vertical",
    },
    plotOptions: {
      series: {
        borderColor: "#e5e7eb",
        borderWidth: 0,
        point: {
          events: {
            click: function () {
              toggleFilter("region", this.properties.region);
              const series = this.series;
              series.points.forEach((p) => {
                p.setState("normal");
                if (p.properties.region === this.properties.region) {
                  p.setState("select");
                }
              });
            },
            mouseOver: function () {
              const series = this.series;
              series.points.forEach((p) => {
                if (p.properties.region === this.properties.region) {
                  p.setState("hover");
                }
              });
            },
            mouseOut: function () {
              const series = this.series;

              series.points.forEach((p) => {
                if (p.destroyed) return;
                p?.setState("normal");
              });
            },
          },
        },
      },
    },

    colorAxis: {
      min: 0,
      minColor: "#bcd4f7",
      maxColor: "#2f5aa6",
    },

    series: hasProjects
      ? [
          {
            name: "Projetos",
            data: mapData,
            joinBy: "hc-key",
            states: {
              hover: {
                color: "#3b82f6",
                borderColor: "#1d4ed8",
              },
              select: {
                color: "#1d4ed8",
                borderWidth: 2,
              },
            },
            dataLabels: {
              enabled: true,
              useHTML: true,
              style: {
                fontSize: "12px",
                fontWeight: "600",
                color: "#111827",
                textOutline: "none",
              },
              // format: "{point.properties.region}",
              formatter: function () {
                if (
                  ["Lisboa", "Coimbra", "Vila Real", "Évora", "Faro"].includes(
                    this.key,
                  )
                )
                  return {
                    Lisboa:
                      '<div style="margin-left:15px; margin-top:3px;">Lisboa</div>',
                    Coimbra:
                      '<div style="margin-left:55px; margin-bottom:20px">Centro</div>',
                    "Vila Real": '<div style="margin-top:10px;">Norte</div>',
                    Évora: '<div style="margin-right:8px;">Alentejo</div>',
                    Faro: '<div style="margin-bottom:8px;">Algarve</div>',
                  }[this.key];
              },
            },
            tooltip: {
              pointFormat: "<b>{point.value}</b> projetos",
            },
          },
        ]
      : [],
  };

  const sizeOrder = ["Micro", "Pequena", "Média", "Grande"];
  const sizeStyle = {
    Micro: { symbol: "circle", color: "#60a5fa" },
    Pequena: { symbol: "circle", color: "#34d399" },
    Média: { symbol: "circle", color: "#f59e0b" },
    Grande: { symbol: "circle", color: "#ef4444" },
  };

  const sizeCounts = filteredData.reduce((acc, d) => {
    const key = d.company_size || "Outros";
    console.log(key);
    acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {});

  const bubbleData = sizeOrder
    .filter((name) => sizeCounts[name] != null)
    .map((name) => ({
      name,
      value: sizeCounts[name],
      color: sizeStyle[name]?.color || "#94a3b8",
      marker: {
        symbol: sizeStyle[name]?.symbol || "circle",
      },
    }));

  const customOptions = {
    chart: {
      type: "packedbubble",
    },
    exporting: {
      enabled: false,
    },
    credits: {
      enabled: false,
    },
    title: {
      text: "",
    },
    tooltip: {
      pointFormat: "<b>{point.name}</b>: {point.value} empresas",
    },
    plotOptions: {
      packedbubble: {
        minSize: "20%",
        maxSize: "100%",
        zMin: 0,
        zMax: Math.max(...bubbleData.map((b) => b.value), 1),
        layoutAlgorithm: {
          splitSeries: false,
          gravitationalConstant: 0.02,
        },
        dataLabels: {
          enabled: true,
          format: "{point.name}\n{point.value}",
          style: {
            color: "#0f172a",
            textOutline: "none",
            fontWeight: "600",
            fontSize: "14px",
          },
        },
        point: {
          events: {
            click: function () {
              toggleFilter("company_size", this.name);
            },
          },
        },
      },
    },
    series: hasProjects
      ? [
          {
            name: "Empresas",
            data: bubbleData,
            showInLegend: false,
          },
        ]
      : [
          {
            name: "Empresas",
            data: [],
            showInLegend: false,
          },
    ],
  };

  const exportPdf = async () => {
    setPdfLoading(true);
    setPdfError("");

    try {
      const response = await apiClient.get("/projects/kpis/pdf/", {
        responseType: "blob",
        validateStatus: () => true,
      });

      if (response.status !== 200) {
        let message = "Falha ao gerar PDF.";

        try {
          const text = await response.data.text();
          const payload = JSON.parse(text);
          message = payload?.detail || payload?.error || message;
        } catch (parseError) {
          void parseError;
        }

        setPdfError(message);
        return;
      }

      const blob = new Blob([response.data], { type: "application/pdf" });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = "kpis-projetos-dashboard.pdf";
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
    } catch (requestError) {
      void requestError;
      setPdfError("Falha ao gerar PDF.");
    } finally {
      setPdfLoading(false);
    }
  };

  return (
    <CContainer>
      <div className="management-filters-header mb-3">
        <div>
          <div className="management-section-title">KPIs de Projetos</div>
          {pdfError ? (
            <div className="small text-danger mt-1">{pdfError}</div>
          ) : null}
        </div>
        <CButton color="primary" variant="outline" onClick={exportPdf} disabled={pdfLoading}>
          {pdfLoading ? (
            <CSpinner size="sm" className="me-2" />
          ) : (
            <CIcon icon={cilCloudDownload} className="me-2" />
          )}
          Exportar PDF
        </CButton>
      </div>

      <CRow className="mb-4 g-3">
        {/* Cartão 1: Projetos, barra de aprovação e Investimento Total */}
        <CCol md={4}>
          <CWidgetStatsD
            title="Real"
            icon={
              <CIcon
                className="my-4 text-white"
                icon={cilBriefcase}
                height={52}
              />
            }
            chart={
              <CChartLine
                className="position-absolute w-100 h-100"
                data={chartDataInvest}
                options={chartOptions}
                customTooltips={false}
              />
            }
            style={{ "--cui-card-cap-bg": "#2f5aa6" }}
            values={[
              {
                title: "Projetos",
                value: formatNumberCompact(totalProjects),
              },
              {
                title: "Investimento Total",
                value: formatCurrencyCompact(
                  financials.totalInvestimentoCandidatura,
                ),
              },
            ]}
          ></CWidgetStatsD>
        </CCol>

        {/* Cartão 2: data da Candidatura (agrupados por mês/ano) */}
        <CCol md={4}>
          <CWidgetStatsD
            icon={
              <CIcon className="my-4 text-white" icon={cilBank} height={52} />
            }
            chart={
              <CChartLine
                className="position-absolute w-100 h-100"
                data={chartDataCandidatura}
                options={chartOptions}
                customTooltips={false}
              />
            }
            style={{ "--cui-card-cap-bg": "#d19a00" }}
            values={[
              {
                title: "Invest. Elegível",
                value: formatCurrencyCompact(
                  financials.totalInvestimentoElegivelCandidatura,
                ),
              },
              {
                title: "Incentivo Previsto",
                value: formatCurrencyCompact(financials.totalIncentivoPrevisto),
              },
            ]}
          />
        </CCol>

        {/* Cartão 3: data Reais (agrupados por mês/ano) com indicador de performance */}
        <CCol md={4}>
          <CWidgetStatsD
            title="Real"
            icon={
              <CIcon
                className="my-4 text-white"
                icon={cilBullhorn}
                height={52}
              />
            }
            chart={
              <CChartLine
                className="position-absolute w-100 h-100"
                data={chartDataApproved}
                options={chartOptions}
                customTooltips={false}
              />
            }
            style={{ "--cui-card-cap-bg": "#1f8a3f" }}
            values={[
              {
                title: "Invest. Elegível",
                value: (
                  <div className="d-flex align-items-baseline gap-2">
                    <span>
                      {formatCurrencyCompact(
                        financials.totalInvestimentoElegivel,
                      )}
                    </span>
                    <small
                      className="fw-semibold"
                      style={{
                        color: getColorForPercentage(approvalInvestRate),
                      }}
                    >
                      {approvalInvestRate.toFixed(1)}%
                    </small>
                  </div>
                ),
              },
              {
                title: "Incentivo Elegível",
                value: (
                  <div className="d-flex align-items-baseline gap-2">
                    <span>
                      {formatCurrencyCompact(financials.totalIncentivoElegivel)}
                    </span>
                    <small
                      className="fw-semibold"
                      style={{
                        color: getColorForPercentage(approvalIncentiveRate),
                      }}
                    >
                      {approvalIncentiveRate.toFixed(1)}%
                    </small>
                  </div>
                ),
              },
            ]}
          ></CWidgetStatsD>
        </CCol>
      </CRow>

      <CCard className={cardClass + " mb-3"}>
        <CCardBody>
          <div className="management-filters-header">
            <div className="management-section-title">Filtros</div>
            <CButton color="light" size="sm" onClick={clearFilters}>
              Limpar filtros
            </CButton>
          </div>
          <div className="d-flex flex-wrap align-items-center gap-2">
            {activeFilters.length > 0 ? (
              <>
                {activeFilters.map((filter) => (
                  <CBadge key={filter.key} color="info" className="text-white">
                    {filter.label}: {filter.value}
                  </CBadge>
                ))}
              </>
            ) : (
              <span className="text-medium-emphasis small">
                Sem filtros ativos
              </span>
            )}
          </div>
        </CCardBody>
      </CCard>

      <CRow className="g-3 mb-3">
        <CCol md={6}>
          <CCard className={cardClass}>
            <CCardBody className={cardBodyClass}>
              <div className="resources-section-title">Estado</div>
              <HighchartsReact
                highcharts={Highcharts}
                options={funnelOptions}
              />
            </CCardBody>
          </CCard>
        </CCol>
        <CCol md={6}>
          <CCard className={cardClass}>
            <CCardBody className={cardBodyClass}>
              <div className="resources-section-title">Tipologia</div>
              <HighchartsReact highcharts={Highcharts} options={barOptions} />
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>

      <CRow className="g-3 mb-3">
        <CCol md={6}>
          <CCard className={cardClass}>
            <CCardBody className={cardBodyClass}>
              <div className="resources-section-title">Setor</div>
              <HighchartsReact highcharts={Highcharts} options={donutOptions} />
            </CCardBody>
          </CCard>
        </CCol>
        <CCol md={6}>
          <CCard className={cardClass}>
            <CCardBody className={cardBodyClass}>
              <div className="resources-section-title">Aviso</div>
              <HighchartsReact
                highcharts={Highcharts}
                options={donut2Options}
              />
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>

      <CRow className="g-3 mb-3">
        <CCol md={12}>
          <CCard className={cardClass}>
            <CCardBody className={cardBodyClass}>
              <div className="resources-section-title">Linha Temporal</div>
              <HighchartsReact
                highcharts={Highcharts}
                options={streamgraphOptions}
              />
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>

      <CRow className="g-3 mb-3">
        <CCol md={6}>
          <CCard className={cardClass}>
            <CCardBody className={cardBodyClass}>
              <div className="resources-section-title">Dimensão</div>
              <HighchartsReact
                highcharts={Highcharts}
                options={customOptions}
              />
            </CCardBody>
          </CCard>
        </CCol>
        <CCol md={6}>
          <CCard className={cardClass}>
            <CCardBody className={cardBodyClass}>
              <div className="resources-section-title">Região</div>
              <HighchartsReact
                highcharts={Highcharts}
                constructorType={"mapChart"}
                options={mapOptions}
              />
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>
      <CRow>
        <CCol md={12}>
          <CCard className="resources-table-card">
            <CCardBody className={cardBodyClass}>
              <div className="resources-section-title">Projetos</div>
              <CTable className="resources-table" striped hover responsive>
                <CTableHead>
                  <CTableRow>
                    <CTableHeaderCell>ID</CTableHeaderCell>
                    <CTableHeaderCell>Nº</CTableHeaderCell>
                    <CTableHeaderCell>Empresa</CTableHeaderCell>
                    <CTableHeaderCell>Tipologia</CTableHeaderCell>
                    <CTableHeaderCell>Aviso</CTableHeaderCell>
                    <CTableHeaderCell>Estado</CTableHeaderCell>
                    <CTableHeaderCell>Submissão</CTableHeaderCell>
                    <CTableHeaderCell>Decisão</CTableHeaderCell>
                    <CTableHeaderCell>Dimensão</CTableHeaderCell>
                    <CTableHeaderCell>Setor</CTableHeaderCell>
                    <CTableHeaderCell>Região</CTableHeaderCell>
                    <CTableHeaderCell>Invest. Candidatura</CTableHeaderCell>
                    <CTableHeaderCell>Invest. Elegível Cand.</CTableHeaderCell>
                    <CTableHeaderCell>Invest. Elegível</CTableHeaderCell>
                    <CTableHeaderCell>Incentivo Previsto</CTableHeaderCell>
                    <CTableHeaderCell>Incentivo Elegível</CTableHeaderCell>
                  </CTableRow>
                </CTableHead>
                <CTableBody>
                  {hasProjects ? (
                    filteredData.map((project) => (
                      <CTableRow
                        key={project.id}
                        className="resources-row"
                        onClick={() =>
                          window.open(
                            `https://streamconsulting.teamwork.com/app/tasks/${project.id}`,
                            "_blank",
                          )
                        }
                      >
                        <CTableDataCell>{project.id}</CTableDataCell>
                        <CTableDataCell>{project.num}</CTableDataCell>
                        <CTableDataCell>{project.companies}</CTableDataCell>
                        <CTableDataCell>
                          {project.typology ? (
                            <CBadge
                              className="resources-chip"
                              style={{
                                backgroundColor: getTypologyColor(
                                  project.typology,
                                ),
                                color: "#fff",
                              }}
                            >
                              {project.typology}
                            </CBadge>
                          ) : (
                            "-"
                          )}
                        </CTableDataCell>
                        <CTableDataCell>
                          {project.notice ? (
                            <CBadge
                              className="resources-chip"
                              style={getNoticeBadgeStyle(project.typology)}
                            >
                              {project.notice}
                            </CBadge>
                          ) : (
                            "-"
                          )}
                        </CTableDataCell>
                        <CTableDataCell>
                          {project.states?.length
                            ? project.states.map((state) => (
                                <CBadge
                                  key={state}
                                  className="resources-chip"
                                  style={{
                                    backgroundColor: getStateColor(state),
                                    color: "#fff",
                                  }}
                                >
                                  {state}
                                </CBadge>
                              ))
                            : "-"}
                        </CTableDataCell>
                        <CTableDataCell>
                          {project.submission_date}
                        </CTableDataCell>
                        <CTableDataCell>{project.decision_date}</CTableDataCell>
                        <CTableDataCell>
                          {project.company_size ? (
                            <CBadge
                              className="resources-chip"
                              style={{
                                backgroundColor:
                                  sizeStyle[project.company_size]?.color ||
                                  "#94a3b8",
                                color: "#fff",
                              }}
                            >
                              {project.company_size}
                            </CBadge>
                          ) : (
                            "-"
                          )}
                        </CTableDataCell>
                        <CTableDataCell>
                          {project.sector ? (
                            <CBadge
                              className="resources-chip"
                              style={{
                                backgroundColor:
                                  colors[
                                    sectorOrder.indexOf(project.sector) %
                                      colors.length
                                  ] || "#94a3b8",
                                color: "#fff",
                              }}
                            >
                              {project.sector}
                            </CBadge>
                          ) : (
                            "-"
                          )}
                        </CTableDataCell>
                        <CTableDataCell>{project.region || "-"}</CTableDataCell>
                        <CTableDataCell>
                          {formatCurrencyCompact(
                            project.financials?.["Investimento Candidatura"],
                          )}
                        </CTableDataCell>
                        <CTableDataCell>
                          {formatCurrencyCompact(
                            project.financials?.[
                              "Investimento Elegível Candidatura"
                            ],
                          )}
                        </CTableDataCell>
                        <CTableDataCell>
                          {formatCurrencyCompact(
                            project.financials?.["Investimento Elegível"],
                          )}
                        </CTableDataCell>
                        <CTableDataCell>
                          {formatCurrencyCompact(
                            project.financials?.["Incentivo Previsto"],
                          )}
                        </CTableDataCell>
                        <CTableDataCell>
                          {formatCurrencyCompact(
                            project.financials?.["Incentivo Elegível"],
                          )}
                        </CTableDataCell>
                      </CTableRow>
                    ))
                  ) : (
                    <CTableRow>
                      <CTableDataCell colSpan={16} className="text-center">
                        Sem dados para apresentar.
                      </CTableDataCell>
                    </CTableRow>
                  )}
                </CTableBody>
              </CTable>
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>
    </CContainer>
  );
};

export default Kpi;
